import * as React from 'react';
import Box from '@mui/material/Box';
import { GRID_CHECKBOX_SELECTION_FIELD } from '@mui/x-data-grid';
import {
  DataGridPro,
  GridColDef,
  GridToolbarContainer,
  GridToolbarExport,
  GridLoadingOverlayProps,
  LoadingOverlayPropsOverrides,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridToolbarProps,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { Button, debounce, LinearProgress } from '@mui/material';
import HeaderElipse from './headerElipse';
import RowButton from './rowButton';
import DropDown from './dropDown';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { useDispatch, useSelector } from '../../state';
import {
  setSearchInput,
  searchCustomers,
  setActiveSelection,
  setLoading,
} from '../../state/customerBrowseSlice';
import type { CustomerSearchInput } from '@repo/api-contract';
import { topNavBarHeight } from '../NavBars/constants';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { openNewCustomerModal } from '../../state/customerModalSlice';
import {
  GRID_CHECKBOX_COLUMN_ORDER,
  usePersistedDataGridColumnPreferences,
} from '../../util/hooks';
import type { DataGridTableId } from '../../util/dataGridTableIds';
import { DataGridTranslatedColumnHeader } from '../dataGrid/DataGridTranslatedColumnHeader';

const TABLE_ID = 'browse-customers' satisfies DataGridTableId;

const CUSTOMER_TABLE_COL_I18N = 'team.Components.CustomerBrowseTable';
const GLOBAL_COMMON_COL_I18N = 'team.Global.Common';

interface LoaderProps
  extends Omit<GridLoadingOverlayProps, 'color'>,
    LoadingOverlayPropsOverrides {
  color?: string;
}

const Loader = (props: LoaderProps) => {
  return <LinearProgress color={'primary'} />;
};

function CustomToolbar(
  props: GridToolbarProps & { selectedRows?: GridRowSelectionModel },
) {
  const { selectedRows = [] } = props;
  const dispatch = useDispatch();
  const { t: tGB } = useTranslation([], { keyPrefix: 'team.Global.Buttons' });
  const { searchInput, searchBy } = useSelector(
    (store) => store.customerBrowseSlice,
  );
  const { team } = useSelector((store) => store.userSlice);

  const debouncedSearch = useCallback(
    debounce((searchQuery: string) => {
      const payload: CustomerSearchInput = {
        query: searchQuery,
        searchBy,
        size: 100,
        page: 0,
      };
      dispatch(searchCustomers(payload));
    }, 1500),
    [searchBy],
  );

  if (team === 'init' || team === 'loading') return null;

  return (
    <GridToolbarContainer>
      <DropDown />
      <TextField
        value={searchInput}
        onChange={(e) => {
          dispatch(setLoading(true));
          dispatch(setSearchInput(e.currentTarget.value));
          debouncedSearch(e.currentTarget.value);
        }}
        id="filled-basic"
        label="Search for customers..."
        variant="outlined"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlinedIcon />
            </InputAdornment>
          ),
        }}
      />
      <Button
        variant="outlined"
        color="primary"
        onClick={() => {
          const payload: CustomerSearchInput = {
            query: searchInput,
            searchBy,
            size: 100,
            page: 0,
          };
          dispatch(searchCustomers(payload));
        }}
      >
        {tGB('Search')}
      </Button>
      <Box sx={{ flexGrow: 1 }} />
      <button
        className="text-blue-600 border-0 bg-inherit hover:cursor-pointer"
        onClick={() => {
          dispatch(openNewCustomerModal());
        }}
      >
        {tGB('+ ADD')}
      </button>
      <GridToolbarExport
        slotProps={{
          tooltip: { title: 'Export data' },
        }}
      />
      <HeaderElipse selectedRows={selectedRows} />
    </GridToolbarContainer>
  );
}

export default function DataTables() {
  const dispatch = useDispatch();
  const { results, loading } = useSelector(
    (store) => store.customerBrowseSlice,
  );
  const { team } = useSelector((store) => store.userSlice);
  const apiRef = useGridApiRef();
  const [rowSelectionModel, setRowSelectionModel] =
    React.useState<GridRowSelectionModel>([]);

  const initialResults = async () => {
    if (team === 'init' || team === 'loading') return null;
    const payload: CustomerSearchInput = {
      query: '',
      searchBy: 'companyName',
      size: 100,
      page: 0,
    };
    await dispatch(searchCustomers(payload));
  };

  React.useEffect(() => {
    initialResults();
  }, []);

  const baseColumns = React.useMemo((): GridColDef[] => {
    return [
      {
        field: 'code',
        type: 'string',
        width: 120,
        headerName: 'Customer Code',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Customer Code"
            params={params}
            keyPrefix={CUSTOMER_TABLE_COL_I18N}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'companyName',
        width: 210,
        headerName: 'Company Name',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Company Name"
            params={params}
            keyPrefix={GLOBAL_COMMON_COL_I18N}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'contact',
        width: 180,
        headerName: 'Contact',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Contact"
            params={params}
            keyPrefix={GLOBAL_COMMON_COL_I18N}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'city',
        type: 'string',
        width: 140,
        headerName: 'City',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="City"
            params={params}
            keyPrefix={GLOBAL_COMMON_COL_I18N}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'state',
        sortable: false,
        width: 100,
        headerName: 'State',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="State"
            params={params}
            keyPrefix={GLOBAL_COMMON_COL_I18N}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'zip',
        type: 'string',
        width: 110,
        headerName: 'Zip',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Zip"
            params={params}
            keyPrefix={GLOBAL_COMMON_COL_I18N}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'telephone',
        type: 'string',
        width: 160,
        headerName: 'Tel. No.',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Tel. No."
            params={params}
            keyPrefix={GLOBAL_COMMON_COL_I18N}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'email',
        type: 'string',
        width: 160,
        headerName: 'Email',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Email"
            params={params}
            keyPrefix={GLOBAL_COMMON_COL_I18N}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'action',
        headerName: '',
        width: 55,
        resizable: false,
        sortable: false,
        renderHeader: () => null,
        renderCell: (params) => {
          return <RowButton customer={params.row.customer}></RowButton>;
        },
      },
    ];
  }, []);

  const persistedPrefs = usePersistedDataGridColumnPreferences({
    tableId: TABLE_ID,
    columns: baseColumns,
    apiRef,
    leadingGridColumnFields: GRID_CHECKBOX_COLUMN_ORDER,
  });

  const columns = React.useMemo(
    () => persistedPrefs.applyPersistedPreferences(baseColumns),
    [baseColumns, persistedPrefs.applyPersistedPreferences],
  );

  return (
    <div style={{ height: `calc(100vh - ${topNavBarHeight} - 250px)` }}>
      <DataGridPro
        apiRef={apiRef}
        columnHeaderHeight={56}
        sx={{
          border: 'none',
          padding: '1rem',
          '.MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: '#ECECEC;',
          },
          '.MuiDataGrid-scrollbar--horizontal': { display: 'none' },
          '.MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
            whiteSpace: 'normal',
            lineHeight: 1.2,
          },
        }}
        rows={
          results
            ? results.results.map((result) => {
                return {
                  id: result.CustomerId,
                  code: result.code,
                  companyName: result.details.companyName || '',
                  contact: result.billing?.contactName || '',
                  city: result.details.city || '',
                  state: result.details.state || '',
                  zip: result.details.zipCode || '',
                  telephone: result.details.phone || '',
                  email: result.details?.email || '',
                  customer: result,
                };
              })
            : []
        }
        loading={loading}
        columns={columns}
        columnVisibilityModel={persistedPrefs.columnVisibilityModel}
        onColumnVisibilityModelChange={
          persistedPrefs.onColumnVisibilityModelChange
        }
        onColumnWidthChange={persistedPrefs.onColumnWidthChange}
        onColumnOrderChange={persistedPrefs.onColumnOrderChange}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 100,
            },
          },
        }}
        pinnedColumns={{
          right: ['action'],
          left: [GRID_CHECKBOX_SELECTION_FIELD],
        }}
        pageSizeOptions={[1]}
        checkboxSelection
        disableRowSelectionOnClick
        disableColumnSorting
        disableColumnFilter
        autoHeight={false}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={setRowSelectionModel}
        slots={{
          loadingOverlay: Loader,
          toolbar: CustomToolbar,
        }}
        slotProps={{
          toolbar: { selectedRows: rowSelectionModel },
        }}
      />
    </div>
  );
}
